import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useAuth } from '../../../auth/AuthContext';
import * as api from '../../../api';
import PageHeader from '../../../app/PageHeader';
import useApiRequest from '../../../hooks/useApiRequest';
import Avatar from '../../../components/primitives/Avatar';
import ConfirmPasswordDialog from '../../../components/feedback/ConfirmPasswordDialog';
import StatePanel from '../../../components/feedback/StatePanel';
import PasswordInput from '../../../components/forms/PasswordInput';
import { validateEmail, validateName, validateUsername } from '../../../utils/accountValidation';
import { fullName } from '../../../utils/names';
import '../AdminUsers.css';

function formFrom(account) {
  return {
    first_name: account.first_name,
    last_name: account.last_name,
    username: account.username,
    email: account.email,
    is_active: account.is_active,
  };
}

/**
 * Starts the administrator password reset: the server emails the other admin a
 * one-time link (finishing it needs their authenticator code). The signed-in admin
 * re-enters their own password first, and never sees the link.
 */
function ResetLinkPanel({ userId, name }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const close = () => {
    setOpen(false);
    setPassword('');
    setError('');
  };

  const send = async (event) => {
    event.preventDefault();
    if (!password) {
      setError('Enter your own password to continue.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api.requestAdminPasswordReset(userId, password);
      setSent(true);
      close();
    } catch (requestError) {
      const data = requestError.data || {};
      setError(
        (Array.isArray(data.current_password) && data.current_password[0]) ||
          requestError.message ||
          'The reset link could not be sent.',
      );
    } finally {
      setBusy(false);
      setPassword('');
    }
  };

  return (
    <section className="card sponsor-detail-card" aria-labelledby="admin-reset-heading">
      <h2 id="admin-reset-heading">Password reset</h2>
      <p>
        Email {name} a one-time link to choose a new password. The link expires quickly, and they
        will also need a code from their authenticator app (or a backup code) to finish.
      </p>
      {sent && (
        <p className="banner banner-success" role="status">
          A reset link was emailed to {name}.
        </p>
      )}
      {!open ? (
        <button
          type="button"
          onClick={() => {
            setSent(false);
            setOpen(true);
          }}
        >
          Send reset link
        </button>
      ) : (
        <form onSubmit={send} noValidate>
          {error && (
            <p className="banner banner-error" role="alert">
              {error}
            </p>
          )}
          <label htmlFor="admin-reset-own-password">Your password</label>
          <PasswordInput
            id="admin-reset-own-password"
            label="Your password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            disabled={busy}
          />
          <div className="sponsor-detail-actions">
            <button type="button" onClick={close} disabled={busy}>
              Cancel
            </button>
            <button className="primary" type="submit" disabled={busy}>
              {busy ? 'Sending…' : 'Send link'}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

export default function AdminAccountPage() {
  const { user } = useAuth();
  const { userId } = useParams();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [confirmingDeactivation, setConfirmingDeactivation] = useState(false);
  const [notice, setNotice] = useState('');

  const {
    data: account,
    setData: setAccount,
    status,
    reload: load,
  } = useApiRequest(
    useCallback(() => api.getAdminAccount(userId), [userId]),
    { skip: user?.account_type !== 'admin', onSuccess: (details) => setForm(formFrom(details)) },
  );

  useEffect(() => {
    document.title = 'Administrator account | Good Driver Incentive Program';
  }, []);

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const cancel = () => {
    setForm(formFrom(account));
    setErrors({});
    setEditing(false);
  };

  // Deactivating locks the owner out, so the server asks for the admin's password.
  const save = async (event, currentPassword) => {
    event?.preventDefault();
    const clientErrors = {
      first_name: validateName(form.first_name, 'first name'),
      last_name: validateName(form.last_name, 'last name'),
      username: validateUsername(form.username),
      email: validateEmail(form.email),
    };
    Object.keys(clientErrors).forEach((field) => {
      if (!clientErrors[field]) delete clientErrors[field];
    });
    if (Object.keys(clientErrors).length) {
      setErrors(clientErrors);
      return;
    }

    if (account.is_active && !form.is_active && currentPassword === undefined) {
      setConfirmingDeactivation(true);
      return;
    }
    setSaving(true);
    setErrors({});
    try {
      const saved = await api.updateAdminAccount(userId, {
        ...form,
        ...(currentPassword ? { current_password: currentPassword } : {}),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        username: form.username.trim(),
        email: form.email.trim(),
      });
      setAccount(saved);
      setForm(formFrom(saved));
      setEditing(false);
      setNotice('Administrator account saved.');
      setConfirmingDeactivation(false);
    } catch (error) {
      // A wrong password is shown in the password dialog, which stays open.
      if (currentPassword !== undefined && (error.data?.current_password || error.status === 429))
        throw error;
      setConfirmingDeactivation(false);
      setErrors(error.data || { detail: error.message });
    } finally {
      setSaving(false);
    }
  };

  if (user?.account_type !== 'admin' || status === 'forbidden')
    return (
      <main className="sponsor-detail-page">
        <StatePanel
          className="sponsor-detail-state"
          headingLevel={1}
          title="You don't have access to this page"
        >
          <p>Only administrators can review other administrator accounts.</p>
        </StatePanel>
      </main>
    );
  if (status === 'loading')
    return (
      <main className="sponsor-detail-page">
        <p role="status">Loading administrator account…</p>
        <section
          className="card sponsor-detail-card sponsor-detail-skeleton"
          aria-label="Loading the account"
        />
      </main>
    );
  if (status === 'error')
    return (
      <main className="sponsor-detail-page">
        <StatePanel
          className="sponsor-detail-state"
          headingLevel={1}
          tone="error"
          title="This account couldn't be loaded"
        >
          <p>Check your connection and try again.</p>
          <button type="button" onClick={load}>
            Try again
          </button>
        </StatePanel>
      </main>
    );
  if (status === 'not-found')
    return (
      <main className="sponsor-detail-page">
        <StatePanel
          className="sponsor-detail-state"
          headingLevel={1}
          title="That administrator account isn't available"
        >
          <p>It may not exist, or you may have selected your own account.</p>
          <Link to="/users">Back to users</Link>
        </StatePanel>
      </main>
    );

  const name = fullName(account);
  return (
    <div className="sponsor-detail-page">
      <PageHeader
        title="Administrator account"
        breadcrumb={
          <>
            <Link to="/users">Users</Link> / {name}
          </>
        }
      />
      <main className="sponsor-detail-content">
        <section className="card sponsor-detail-card">
          <div className="sponsor-detail-card-head">
            <div className="sponsor-detail-person">
              <Avatar className="sponsor-detail-avatar" name={name} />
              <div>
                <h2>{name}</h2>
                <p>
                  @{account.username} <span>Admin</span> ·{' '}
                  {account.is_active ? 'Active' : 'Inactive'}
                </p>
              </div>
            </div>
            {!editing && (
              <div className="sponsor-detail-header-actions">
                <button
                  type="button"
                  onClick={() => {
                    setNotice('');
                    setErrors({});
                    setEditing(true);
                  }}
                >
                  Edit account
                </button>
              </div>
            )}
          </div>
          {notice && (
            <p className="banner banner-success sponsor-detail-banner" role="status">
              {notice}
            </p>
          )}
          {!editing ? (
            <dl className="sponsor-detail-view">
              <div>
                <dt>Full name</dt>
                <dd>{name}</dd>
              </div>
              <div>
                <dt>Username</dt>
                <dd>@{account.username}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>{account.email}</dd>
              </div>
              <div>
                <dt>Account status</dt>
                <dd>{account.is_active ? 'Active' : 'Inactive'}</dd>
              </div>
              <div>
                <dt>Account type</dt>
                <dd>
                  Administrator <small>Locked after account creation</small>
                </dd>
              </div>
            </dl>
          ) : (
            <form onSubmit={save} noValidate>
              {errors.detail && (
                <p className="banner banner-error sponsor-detail-banner" role="alert">
                  {errors.detail}
                </p>
              )}
              <div className="sponsor-detail-form">
                <label>
                  First name
                  <input
                    value={form.first_name}
                    onChange={(event) => update('first_name', event.target.value)}
                    maxLength="50"
                    autoComplete="given-name"
                  />
                  {errors.first_name && <small>{errors.first_name}</small>}
                </label>
                <label>
                  Last name
                  <input
                    value={form.last_name}
                    onChange={(event) => update('last_name', event.target.value)}
                    maxLength="50"
                    autoComplete="family-name"
                  />
                  {errors.last_name && <small>{errors.last_name}</small>}
                </label>
                <label>
                  Username
                  <input
                    value={form.username}
                    onChange={(event) => update('username', event.target.value)}
                    maxLength="30"
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck="false"
                  />
                  {errors.username && <small>{errors.username}</small>}
                </label>
                <label>
                  Email
                  <input
                    type="email"
                    value={form.email}
                    onChange={(event) => update('email', event.target.value)}
                    maxLength="254"
                    autoComplete="email"
                    autoCapitalize="none"
                    spellCheck="false"
                  />
                  {errors.email && <small>{errors.email}</small>}
                </label>
                <label>
                  Account status
                  <select
                    value={String(form.is_active)}
                    onChange={(event) => update('is_active', event.target.value === 'true')}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                  <small>An inactive account cannot sign in.</small>
                </label>
                <div className="sponsor-detail-locked">
                  <span>Account type</span>
                  <strong>Administrator</strong>
                  <small>Role and administrative privileges cannot be changed here.</small>
                </div>
              </div>
              <div className="sponsor-detail-actions">
                <button type="button" onClick={cancel} disabled={saving}>
                  Cancel
                </button>
                <button className="primary" type="submit" disabled={saving}>
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          )}
        </section>
        {!editing && <ResetLinkPanel userId={userId} name={name} />}
      </main>
      {confirmingDeactivation && (
        <ConfirmPasswordDialog
          title="Deactivate this account?"
          message={`${name} won't be able to sign in until the account is made active again. Enter your password to confirm.`}
          confirmLabel="Deactivate account"
          onConfirm={(password) => save(null, password)}
          onCancel={() => setConfirmingDeactivation(false)}
        />
      )}
    </div>
  );
}
